import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  Tooltip,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Science, Category, Public, LocalOffer, Info } from '@mui/icons-material';
import { motion } from 'framer-motion';
import AtomAnimation from '../components/AtomAnimation';
import MineralDetailModal from '../components/MineralDetailModal';

/**
 * EnhancedMineralsPage component displays detailed information about minerals
 * with scientific names, images, and animations
 */
const EnhancedMineralsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [selectedMineral, setSelectedMineral] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenModal = (mineral) => {
    setSelectedMineral(mineral);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Mineral data with scientific names, symbols, and detailed information
  const mineralCategories = [
    {
      name: "Rare Earth Elements",
      description: "Critical rare earth minerals essential for high-tech applications and clean energy technologies",
      minerals: [
        {
          name: "Samarium",
          symbol: "Sm",
          atomicNumber: 62,
          category: "Lanthanide",
          image: "/images/minerals/samarium.jpg",
          description: "Used in permanent magnets, lasers, and nuclear reactors. Critical for precision-guided weapons and cancer treatments.",
          uses: ["Permanent magnets", "Lasers", "Nuclear reactors", "Cancer treatments"],
          properties: "Silvery-white, moderately hard rare earth metal that oxidizes in air",
          sources: ["China", "Australia", "United States", "India"]
        },
        {
          name: "Gadolinium",
          symbol: "Gd",
          atomicNumber: 64,
          category: "Lanthanide",
          image: "/images/minerals/gadolinium.jpg",
          description: "Used in MRI contrast agents, nuclear reactors, and data storage. Has unique magnetic properties at room temperature.",
          uses: ["MRI contrast agents", "Nuclear reactors", "Data storage", "Microwave applications"],
          properties: "Silvery-white, malleable and ductile rare earth metal with unique magnetic properties",
          sources: ["China", "Russia", "Australia", "Malaysia"]
        },
        {
          name: "Terbium",
          symbol: "Tb",
          atomicNumber: 65,
          category: "Lanthanide",
          image: "/images/minerals/terbium.jpg",
          description: "Essential for solid-state devices, fuel cells, and naval sonar systems. Used in green phosphors for displays.",
          uses: ["Green phosphors", "Solid-state devices", "Fuel cells", "Naval sonar systems"],
          properties: "Silvery-white, rare earth metal that is malleable, ductile, and soft enough to be cut with a knife",
          sources: ["China", "Australia", "United States", "Myanmar"]
        },
        {
          name: "Dysprosium",
          symbol: "Dy",
          atomicNumber: 66,
          category: "Lanthanide",
          image: "/images/minerals/dysprosium.jpg",
          description: "Critical for neodymium-based permanent magnets used in electric vehicles and wind turbines. Enhances coercivity at high temperatures.",
          uses: ["Permanent magnets", "Electric vehicles", "Wind turbines", "Data storage devices"],
          properties: "Bright silver rare earth metal with high magnetic susceptibility",
          sources: ["China", "Australia", "Brazil", "Vietnam"]
        },
        {
          name: "Lutetium",
          symbol: "Lu",
          atomicNumber: 71,
          category: "Lanthanide",
          image: "/images/minerals/lutetium.jpg",
          description: "Used in petroleum refining catalysts, positron emission tomography (PET) scans, and cancer therapy. The most expensive rare earth element.",
          uses: ["Petroleum catalysts", "PET scan detectors", "Cancer therapy", "Phosphors"],
          properties: "Silvery-white, the hardest and densest of the lanthanides, with the highest melting point",
          sources: ["China", "Australia", "United States", "Russia"]
        },
        {
          name: "Scandium",
          symbol: "Sc",
          atomicNumber: 21,
          category: "Transition Metal",
          image: "/images/minerals/scandium.jpg",
          description: "Used in aerospace components, sports equipment, and solid oxide fuel cells. Strengthens aluminum alloys significantly.",
          uses: ["Aluminum alloys", "Aerospace components", "Sports equipment", "Solid oxide fuel cells"],
          properties: "Silvery-white, lightweight transition metal that develops a yellowish or pinkish cast when oxidized",
          sources: ["China", "Kazakhstan", "Russia", "Ukraine"]
        },
        {
          name: "Yttrium",
          symbol: "Y",
          atomicNumber: 39,
          category: "Transition Metal",
          image: "/images/minerals/yttrium.jpg",
          description: "Essential for LED and OLED red phosphors, superconductors, and cancer treatments. Used in the manufacturing of camera lenses.",
          uses: ["Red phosphors", "Superconductors", "Cancer treatments", "Camera lenses"],
          properties: "Silvery-white, chemically similar to lanthanides, with high thermal stability",
          sources: ["China", "Australia", "United States", "India"]
        }
      ]
    },
    {
      name: "Critical Minerals",
      description: "Essential minerals for modern technology and clean energy",
      minerals: [
        {
          name: "Cobalt",
          symbol: "Co",
          atomicNumber: 27,
          category: "Transition Metal",
          image: "/images/minerals/cobalt.jpg",
          description: "Essential for rechargeable batteries and electric vehicles. A key component in the transition to clean energy.",
          uses: ["Lithium-ion batteries", "Superalloys", "Magnets", "Catalysts"],
          properties: "Hard, lustrous, silver-gray metal with magnetic properties",
          sources: ["Democratic Republic of Congo", "Australia", "Cuba", "Philippines"]
        },
        {
          name: "Lithium",
          symbol: "Li",
          atomicNumber: 3,
          category: "Alkali Metal",
          image: "/images/minerals/lithium.jpg",
          description: "Key component in batteries for electronics and electric vehicles. Critical for energy storage solutions.",
          uses: ["Rechargeable batteries", "Glass and ceramics", "Lubricating greases", "Pharmaceuticals"],
          properties: "Soft, silver-white metal, lightest solid element",
          sources: ["Australia", "Chile", "China", "Argentina"]
        },
        {
          name: "Copper",
          symbol: "Cu",
          atomicNumber: 29,
          category: "Transition Metal",
          image: "/images/minerals/copper.jpg",
          description: "Vital for electrical wiring, electronics, and construction. One of the most versatile industrial metals.",
          uses: ["Electrical wiring", "Electronics", "Construction", "Transportation"],
          properties: "Reddish-orange, ductile metal with high thermal and electrical conductivity",
          sources: ["Chile", "Peru", "China", "United States"]
        },
        {
          name: "Rare Earth Elements",
          symbol: "REE",
          atomicNumber: "57-71",
          category: "Lanthanides",
          image: "/images/minerals/rare-earth.jpg",
          description: "Critical for high-tech applications and renewable energy. Includes neodymium, dysprosium, and praseodymium.",
          uses: ["Permanent magnets", "Catalysts", "Phosphors", "Polishing compounds"],
          properties: "Group of 17 elements with similar properties",
          sources: ["China", "Australia", "United States", "Myanmar"]
        },
        {
          name: "Graphite",
          symbol: "C",
          atomicNumber: 6,
          category: "Non-metal",
          image: "/images/minerals/graphite.jpg",
          description: "Essential for lithium-ion batteries, fuel cells, and various high-tech applications.",
          uses: ["Lithium-ion batteries", "Refractories", "Lubricants", "Pencils"],
          properties: "Soft, black mineral composed of carbon with high electrical conductivity",
          sources: ["China", "Brazil", "Mozambique", "Madagascar"]
        },
        {
          name: "Nickel",
          symbol: "Ni",
          atomicNumber: 28,
          category: "Transition Metal",
          image: "/images/minerals/nickel.jpg",
          description: "Important for stainless steel and batteries. A key component in the growing electric vehicle market.",
          uses: ["Stainless steel", "Batteries", "Alloys", "Electroplating"],
          properties: "Silvery-white, hard, ductile metal with magnetic properties",
          sources: ["Indonesia", "Philippines", "Russia", "New Caledonia"]
        }
      ]
    },
    {
      name: "Precious Metals",
      description: "Rare, naturally occurring metallic elements of high economic value",
      minerals: [
        {
          name: "Gold",
          symbol: "Au",
          atomicNumber: 79,
          category: "Transition Metal",
          image: "/images/minerals/gold.jpg",
          description: "Used in jewelry, electronics, and as a store of value. One of the most sought-after precious metals.",
          uses: ["Jewelry", "Investment", "Electronics", "Dentistry"],
          properties: "Bright yellow, dense, soft, malleable, and ductile metal",
          sources: ["China", "Australia", "Russia", "United States"]
        },
        {
          name: "Silver",
          symbol: "Ag",
          atomicNumber: 47,
          category: "Transition Metal",
          image: "/images/minerals/silver.jpg",
          description: "Important for electronics, solar panels, and medical applications. Has both industrial and investment value.",
          uses: ["Electronics", "Solar panels", "Photography", "Jewelry"],
          properties: "Lustrous, soft, white, and ductile metal with highest electrical conductivity",
          sources: ["Mexico", "Peru", "China", "Australia"]
        },
        {
          name: "Platinum",
          symbol: "Pt",
          atomicNumber: 78,
          category: "Transition Metal",
          image: "/images/minerals/platinum.jpg",
          description: "Critical for catalytic converters and fuel cells. A rare and valuable metal with unique properties.",
          uses: ["Catalytic converters", "Jewelry", "Electronics", "Fuel cells"],
          properties: "Dense, malleable, ductile, highly unreactive, silvery-white metal",
          sources: ["South Africa", "Russia", "Zimbabwe", "Canada"]
        },
        {
          name: "Palladium",
          symbol: "Pd",
          atomicNumber: 46,
          category: "Transition Metal",
          image: "/images/minerals/palladium.jpg",
          description: "Essential for automotive catalysts and electronics. One of the platinum group metals with growing demand.",
          uses: ["Catalytic converters", "Electronics", "Dental fillings", "Jewelry"],
          properties: "Least dense and lowest melting point of platinum group metals",
          sources: ["Russia", "South Africa", "Canada", "United States"]
        },
        {
          name: "Rhodium",
          symbol: "Rh",
          atomicNumber: 45,
          category: "Transition Metal",
          image: "/images/minerals/rhodium.jpg",
          description: "Used in catalytic converters and as a finish for jewelry. One of the rarest and most valuable precious metals.",
          uses: ["Catalytic converters", "Jewelry plating", "Optical instruments", "Electrical contacts"],
          properties: "Silvery-white, hard, corrosion-resistant noble metal",
          sources: ["South Africa", "Russia", "Zimbabwe", "Canada"]
        }
      ]
    },
    {
      name: "Industrial Minerals",
      description: "Minerals used in manufacturing and industrial processes",
      minerals: [
        {
          name: "Tin",
          symbol: "Sn",
          atomicNumber: 50,
          category: "Post-transition Metal",
          image: "/images/minerals/tin.jpg",
          description: "Used in soldering, electronics, and chemicals. An essential component in many manufacturing processes.",
          uses: ["Soldering", "Tin plating", "Chemicals", "Alloys"],
          properties: "Silvery, malleable post-transition metal that resists corrosion",
          sources: ["China", "Indonesia", "Myanmar", "Brazil"]
        },
        {
          name: "Tungsten",
          symbol: "W",
          atomicNumber: 74,
          category: "Transition Metal",
          image: "/images/minerals/tungsten.jpg",
          description: "Critical for hardened steel and high-temperature applications. Known for its exceptional hardness and heat resistance.",
          uses: ["Cemented carbides", "Steel alloys", "Electrical applications", "Radiation shielding"],
          properties: "Steel-gray to tin-white metal with the highest melting point of all elements",
          sources: ["China", "Vietnam", "Russia", "Bolivia"]
        },
        {
          name: "Tantalum",
          symbol: "Ta",
          atomicNumber: 73,
          category: "Transition Metal",
          image: "/images/minerals/tantalum.jpg",
          description: "Essential for electronic components and medical implants. A conflict mineral with strict sourcing requirements.",
          uses: ["Electronic capacitors", "Superalloys", "Medical implants", "Cutting tools"],
          properties: "Blue-gray, heavy, and very hard metal with high corrosion resistance",
          sources: ["Rwanda", "Democratic Republic of Congo", "Brazil", "Australia"]
        },
        {
          name: "Zinc",
          symbol: "Zn",
          atomicNumber: 30,
          category: "Transition Metal",
          image: "/images/minerals/zinc.jpg",
          description: "Used in galvanizing, die-casting, and brass production. Essential for protecting steel from corrosion.",
          uses: ["Galvanizing", "Brass and bronze", "Die-casting", "Rubber and chemicals"],
          properties: "Bluish-white, lustrous metal that is brittle at room temperature",
          sources: ["China", "Australia", "Peru", "United States"]
        },
        {
          name: "Manganese",
          symbol: "Mn",
          atomicNumber: 25,
          category: "Transition Metal",
          image: "/images/minerals/manganese.jpg",
          description: "Essential for steel production and batteries. Used in aluminum alloys and as a pigment.",
          uses: ["Steel production", "Batteries", "Aluminum alloys", "Fertilizers"],
          properties: "Hard, brittle, silvery metal that can be passivated",
          sources: ["South Africa", "Australia", "Gabon", "China"]
        },
        {
          name: "Chromium",
          symbol: "Cr",
          atomicNumber: 24,
          category: "Transition Metal",
          image: "/images/minerals/chromium.jpg",
          description: "Used in stainless steel, chrome plating, and pigments. Essential for corrosion resistance.",
          uses: ["Stainless steel", "Chrome plating", "Pigments", "Leather tanning"],
          properties: "Steely-gray, lustrous, hard metal with high corrosion resistance",
          sources: ["South Africa", "Kazakhstan", "India", "Turkey"]
        }
      ]
    },
    {
      name: "Battery Minerals",
      description: "Minerals essential for battery production and energy storage",
      minerals: [
        {
          name: "Lithium",
          symbol: "Li",
          atomicNumber: 3,
          category: "Alkali Metal",
          image: "/images/minerals/lithium.jpg",
          description: "Primary component in lithium-ion batteries. Critical for electric vehicles and energy storage.",
          uses: ["Lithium-ion batteries", "Glass and ceramics", "Lubricating greases", "Pharmaceuticals"],
          properties: "Soft, silver-white metal, lightest solid element",
          sources: ["Australia", "Chile", "China", "Argentina"]
        },
        {
          name: "Cobalt",
          symbol: "Co",
          atomicNumber: 27,
          category: "Transition Metal",
          image: "/images/minerals/cobalt.jpg",
          description: "Key component in lithium-ion batteries. Provides stability and high energy density.",
          uses: ["Lithium-ion batteries", "Superalloys", "Magnets", "Catalysts"],
          properties: "Hard, lustrous, silver-gray metal with magnetic properties",
          sources: ["Democratic Republic of Congo", "Australia", "Cuba", "Philippines"]
        },
        {
          name: "Nickel",
          symbol: "Ni",
          atomicNumber: 28,
          category: "Transition Metal",
          image: "/images/minerals/nickel.jpg",
          description: "Used in lithium-ion batteries to increase energy density. Critical for electric vehicle batteries.",
          uses: ["Lithium-ion batteries", "Stainless steel", "Alloys", "Electroplating"],
          properties: "Silvery-white, hard, ductile metal with magnetic properties",
          sources: ["Indonesia", "Philippines", "Russia", "New Caledonia"]
        },
        {
          name: "Manganese",
          symbol: "Mn",
          atomicNumber: 25,
          category: "Transition Metal",
          image: "/images/minerals/manganese.jpg",
          description: "Used in various battery technologies including lithium-ion and alkaline batteries.",
          uses: ["Batteries", "Steel production", "Aluminum alloys", "Fertilizers"],
          properties: "Hard, brittle, silvery metal that can be passivated",
          sources: ["South Africa", "Australia", "Gabon", "China"]
        },
        {
          name: "Graphite",
          symbol: "C",
          atomicNumber: 6,
          category: "Non-metal",
          image: "/images/minerals/graphite.jpg",
          description: "Used as anode material in lithium-ion batteries. Essential for battery production.",
          uses: ["Lithium-ion batteries", "Refractories", "Lubricants", "Pencils"],
          properties: "Soft, black mineral composed of carbon with high electrical conductivity",
          sources: ["China", "Brazil", "Mozambique", "Madagascar"]
        },
        {
          name: "Vanadium",
          symbol: "V",
          atomicNumber: 23,
          category: "Transition Metal",
          image: "/images/minerals/vanadium.jpg",
          description: "Used in vanadium redox flow batteries for grid-scale energy storage.",
          uses: ["Flow batteries", "Steel alloys", "Catalysts", "Aerospace applications"],
          properties: "Soft, ductile, silver-blue metal with good corrosion resistance",
          sources: ["China", "Russia", "South Africa", "Brazil"]
        }
      ]
    }
  ];

  // Render a mineral card with scientific information and animations
  const renderMineralCard = (mineral) => {
    // Generate a color based on the mineral's category
    const getCategoryColor = (category) => {
      const categoryColors = {
        'Transition Metal': '#3b82f6', // blue
        'Alkali Metal': '#ef4444',     // red
        'Post-transition Metal': '#8b5cf6', // purple
        'Lanthanides': '#ec4899',      // pink
        'Lanthanide': '#ec4899',       // pink (same as Lanthanides)
        'Non-metal': '#10b981',        // green
        'default': '#6b7280'           // gray
      };

      return categoryColors[category] || categoryColors.default;
    };

    const mineralColor = getCategoryColor(mineral.category);

    // Calculate number of electrons based on atomic number
    const getElectrons = (atomicNumber) => {
      // For simplicity, just use the atomic number if it's a single value
      if (typeof atomicNumber === 'number') {
        return Math.min(atomicNumber, 8); // Limit to 8 for visual clarity
      }
      // For ranges like "57-71" for rare earth elements, use a default
      return 6;
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ height: '100%' }}
      >
        <Card
          elevation={3}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: 8
            }
          }}
        >
          {/* Atomic Animation instead of image */}
          <Box sx={{
            height: 220,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
            borderBottom: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
          }}>
            <AtomAnimation
              symbol={mineral.symbol}
              electrons={getElectrons(mineral.atomicNumber)}
              color={mineralColor}
              size={180}
            />
          </Box>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" component="h3">
                {mineral.name}
              </Typography>
              <Tooltip title="Chemical Symbol" placement="top">
                <Chip
                  label={mineral.symbol}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, fontWeight: 'bold' }}
                  icon={<Science fontSize="small" />}
                />
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              <Tooltip title="Atomic Number" placement="top">
                <Chip
                  label={`Atomic #: ${mineral.atomicNumber}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              </Tooltip>
              <Tooltip title="Category" placement="top">
                <Chip
                  label={mineral.category}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                  icon={<Category fontSize="small" />}
                />
              </Tooltip>
            </Box>



            <Typography variant="body2" color="text.secondary" paragraph>
              {mineral.description}
            </Typography>

            <Typography variant="subtitle2" color="primary" gutterBottom>
              Primary Uses:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {mineral.uses.slice(0, 3).map((use, index) => (
                <Chip
                  key={index}
                  label={use}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Box>

            <Typography variant="subtitle2" color="primary" gutterBottom>
              Major Sources:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {mineral.sources.slice(0, 2).map((source, index) => (
                <Chip
                  key={index}
                  label={source}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                  icon={<Public fontSize="small" />}
                />
              ))}
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
            <Button
              size="small"
              endIcon={<Info />}
              onClick={() => handleOpenModal(mineral)}
              sx={{
                color: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(234, 179, 8, 0.08)' }
              }}
            >
              Learn More
            </Button>
          </CardActions>
        </Card>
      </motion.div>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Minerals Trading Platform
        </Typography>
        <Typography variant="h6" component="h2" color="text.secondary" sx={{ mb: 4, maxWidth: 900, mx: 'auto' }}>
          Explore our comprehensive catalog of minerals available for secure and transparent trading on the Laprecio platform
        </Typography>
        <Typography variant="body1" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Our blockchain-verified platform ensures compliance, traceability, and ethical sourcing for all mineral transactions.
          Connect directly with verified miners, processors, and buyers worldwide.
        </Typography>
      </Box>

      {/* Tabs for mineral categories */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          centered={!isMobile}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'bold',
              fontSize: isMobile ? '0.8rem' : '0.9rem'
            },
            '& .Mui-selected': {
              color: 'primary.main'
            }
          }}
        >
          {mineralCategories.map((category, index) => (
            <Tab
              key={index}
              label={category.name}
              id={`mineral-tab-${index}`}
              aria-controls={`mineral-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Mineral category content */}
      {mineralCategories.map((category, index) => (
        <div
          key={index}
          role="tabpanel"
          hidden={tabValue !== index}
          id={`mineral-tabpanel-${index}`}
          aria-labelledby={`mineral-tab-${index}`}
        >
          {tabValue === index && (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom sx={{
                  borderBottom: '2px solid #eab308',
                  pb: 1,
                  display: 'inline-block'
                }}>
                  {category.name}
                </Typography>
                <Typography variant="body1" paragraph>
                  {category.description}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {category.minerals.map((mineral, idx) => (
                  <Grid item xs={12} sm={6} md={4} lg={4} key={idx}>
                    {renderMineralCard(mineral)}
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </div>
      ))}

      {/* CTA Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mt: 8,
          mb: 4,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Typography variant="h4" component="h2" gutterBottom>
          Trade Minerals Securely and Transparently
        </Typography>

        <Typography variant="body1" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Join Laprecio's minerals trading platform to connect directly with verified miners, processors, and buyers worldwide.
          Ensure compliance with regulations and meet sustainability goals.
        </Typography>

        <Button
          component={RouterLink}
          to="/register"
          variant="contained"
          color="primary"
          size="large"
          sx={{
            mt: 2,
            backgroundColor: '#eab308',
            '&:hover': {
              backgroundColor: '#d69e2e'
            },
            px: 4,
            py: 1.5
          }}
        >
          Register Interest
        </Button>
      </Paper>

      {/* Resources Section */}
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{
          borderBottom: '2px solid #eab308',
          pb: 1,
          color: 'primary.dark'
        }}>
          Mineral Trading Resources
        </Typography>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Laprecio provides a comprehensive platform for trading various minerals with full compliance and traceability.
          Our platform supports trading of critical minerals, precious metals, industrial minerals, and battery minerals from verified sources worldwide.
        </Typography>

        <Typography variant="body1" paragraph>
          All minerals traded on our platform are subject to rigorous verification processes to ensure they meet international standards and regulations, including:
        </Typography>

        <List sx={{ pl: 2 }}>
          <ListItem>
            <ListItemText
              primary="OECD Due Diligence Guidance for Responsible Supply Chains"
              secondary="Framework for companies to identify and address risks in their mineral supply chains"
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText
              primary="EU Conflict Minerals Regulation"
              secondary="European Union regulation to ensure responsible sourcing of minerals from conflict-affected areas"
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText
              primary="Dodd-Frank Act Section 1502"
              secondary="U.S. legislation requiring companies to disclose their use of conflict minerals"
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText
              primary="Responsible Minerals Assurance Process (RMAP)"
              secondary="Independent third-party audit program for responsible mineral sourcing"
            />
          </ListItem>
        </List>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Our blockchain-based verification system provides end-to-end traceability for all minerals, ensuring transparency and accountability throughout the supply chain.
        </Typography>
      </Paper>

      {/* Mineral Detail Modal */}
      <MineralDetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        mineral={selectedMineral}
      />
    </Container>
  );
};

export default EnhancedMineralsPage;
